import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { FileText, Loader2, ExternalLink, Circle, X } from 'lucide-react';
import './Summaries.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const summaryTypes = [
  { id: 'general', label: 'General', description: 'Comprehensive summary' },
  { id: 'executive', label: 'Executive Brief', description: '3-4 bullets, key risks & opportunities' },
  { id: 'diligence', label: 'Due Diligence Summary', description: 'Structured: Overview, Findings, Risks' },
  { id: 'clauses', label: 'Key Clauses', description: 'Important legal clauses only' },
  { id: 'financial', label: 'Financial Snapshot', description: 'Key metrics, trends, red flags' },
];

function Summaries({ onOpenAnnotate }) {
  const [documents, setDocuments] = useState([]);
  const [deals, setDeals] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState('');
  const [summaryType, setSummaryType] = useState('general');
  const [topic, setTopic] = useState('');
  const [viewingSummary, setViewingSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState('');
  const [docPreview, setDocPreview] = useState(null);

  const loadingSteps = [
    'Reading document...',
    'Analyzing content...',
    'Generating summary...'
  ];

  useEffect(() => {
    fetchDocuments();
    fetchDeals();
    loadSummaries();
  }, []);

  useEffect(() => {
    if (selectedDoc) {
      fetchDocumentPreview(selectedDoc);
    } else {
      setDocPreview(null);
    }
  }, [selectedDoc]);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setLoadingStep(prev => {
          if (prev < loadingSteps.length - 1) return prev + 1;
          return prev;
        });
      }, 2500);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents`);
      const docs = response.data.documents || response.data || [];
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/deals`);
      setDeals(response.data);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  };

  const fetchDocumentPreview = async (jobId) => {
    try {
      const doc = documents.find(d => d.jobId === jobId);
      if (!doc) return;

      // Simple preview - first 500 chars of text
      const previewText = doc.text?.substring(0, 500) || '';
      const wordCount = doc.text?.split(/\s+/).length || 0;
      
      // Validation checks
      let validationStatus = 'good';
      let validationMessage = 'Parsed correctly';
      
      if (!doc.text || doc.text.length < 100) {
        validationStatus = 'error';
        validationMessage = 'Limited content - document may be scanned or encrypted';
      } else if (doc.text.match(/^(PDF|%!PDF|%PDF|<?xml)/i) || previewText.length < 50) {
        validationStatus = 'warning';
        validationMessage = 'May contain mostly metadata - check preview';
      }

      setDocPreview({
        filename: doc.filename,
        preview: previewText,
        wordCount,
        validationStatus,
        validationMessage,
        dealName: doc.dealName || null
      });
    } catch (error) {
      console.error('Failed to fetch preview:', error);
    }
  };

  const loadSummaries = () => {
    const stored = localStorage.getItem('dealnexa_summaries');
    if (stored) {
      setSummaries(JSON.parse(stored));
    }
  };

  const saveSummaries = (newSummaries) => {
    localStorage.setItem('dealnexa_summaries', JSON.stringify(newSummaries));
    setSummaries(newSummaries);
  };

  const handleCreateSummary = async () => {
    if (!selectedDoc) return;

    setLoading(true);
    setLoadingStep(0);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/summarize`, {
        jobId: selectedDoc,
        topic: topic || '',
        summaryType: summaryType,
      });

      const doc = documents.find(d => d.jobId === selectedDoc);
      const newSummary = {
        id: Date.now(),
        documentId: selectedDoc,
        documentName: doc?.filename || 'Unknown Document',
        summaryType: summaryType,
        summaryTypeLabel: summaryTypes.find(t => t.id === summaryType)?.label || 'General',
        topic: topic || 'General',
        createdAt: new Date().toISOString(),
        content: response.data.summary,
        qualityFlag: response.data.qualityFlag || null,
      };

      const updatedSummaries = [newSummary, ...summaries];
      saveSummaries(updatedSummaries);
      setTopic('');
    } catch (err) {
      console.error('Summary generation failed:', err);
      setError(err.response?.data?.error || 'Failed to generate summary. Please try again.');
    } finally {
      setLoading(false);
      setLoadingStep(0);
    }
  };

  const handleDeleteSummary = (id) => {
    const updatedSummaries = summaries.filter(s => s.id !== id);
    saveSummaries(updatedSummaries);
  };

  const handleDeleteAll = () => {
    if (window.confirm('Are you sure you want to delete all summaries?')) {
      saveSummaries([]);
    }
  };

  const handleOpenDocument = (documentId) => {
    if (onOpenAnnotate) {
      onOpenAnnotate(documentId);
    }
  };

  // Group documents by deal
  const getGroupedDocuments = () => {
    const unlinked = documents.filter(d => !d.dealId);
    const grouped = [];
    
    // Add unlinked documents group
    if (unlinked.length > 0) {
      grouped.push({
        type: 'unlinked',
        label: 'Unlinked Documents',
        documents: unlinked
      });
    }
    
    // Add deal groups
    deals.forEach(deal => {
      const dealDocs = documents.filter(d => d.dealId === deal.id);
      if (dealDocs.length > 0) {
        grouped.push({
          type: 'deal',
          label: deal.name,
          dealId: deal.id,
          documents: dealDocs
        });
      }
    });
    
    return grouped;
  };

  const getValidationIcon = (status) => {
    switch (status) {
      case 'good':
        return <Circle size={8} fill="#059669" color="#059669" className="validation-icon" />;
      case 'warning':
        return <Circle size={8} fill="#D97706" color="#D97706" className="validation-icon" />;
      case 'error':
        return <Circle size={8} fill="#DC2626" color="#DC2626" className="validation-icon" />;
      default:
        return null;
    }
  };

  const groupedDocs = getGroupedDocuments();

  return (
    <div className="summaries-container">
      <div className="summaries-header">
        <div className="page-title">
          <div className="page-title-icon"><FileText size={20} /></div>
          <h1>Summaries</h1>
        </div>
      </div>

      <div className="create-summary-card">
        <h3>Create New Summary</h3>

        {documents.length === 0 ? (
          <div className="summaries-empty">
            <p>No documents uploaded yet. Upload a document first to generate summaries.</p>
          </div>
        ) : (
          <>
            <div className="form-row">
              <div className="form-group flex-2">
                <label>Document</label>
                <select
                  value={selectedDoc}
                  onChange={(e) => setSelectedDoc(e.target.value)}
                  className="grouped-select"
                >
                  <option value="">Select a document...</option>
                  {groupedDocs.map(group => (
                    <optgroup key={group.type === 'unlinked' ? 'unlinked' : group.dealId} label={group.label}>
                      {group.documents.map(doc => (
                        <option key={doc.jobId} value={doc.jobId}>
                          {doc.filename}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="form-group flex-1">
                <label>Summary Type</label>
                <select
                  value={summaryType}
                  onChange={(e) => setSummaryType(e.target.value)}
                >
                  {summaryTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="hint-text">{summaryTypes.find(t => t.id === summaryType)?.description}</p>
              </div>
            </div>

            {docPreview && (
              <div className="document-preview-panel">
                <div className="preview-header">
                  <span className="preview-filename">{docPreview.filename}</span>
                  <span className="preview-meta">
                    {docPreview.wordCount.toLocaleString()} words
                    {docPreview.dealName && ` • ${docPreview.dealName}`}
                  </span>
                </div>
                <div className={`validation-badge ${docPreview.validationStatus}`}>
                  {getValidationIcon(docPreview.validationStatus)}
                  <span>{docPreview.validationMessage}</span>
                </div>
                <div className="preview-text">
                  {docPreview.preview}
                  {docPreview.preview.length >= 500 && '...'}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Focus Topic (Optional)</label>
              <input
                type="text"
                placeholder="e.g., Financial risks, Key personnel, Contract terms"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
              <p className="hint-text">Narrow the summary to a specific topic or section</p>
            </div>

            {error && <div className="summary-error">{error}</div>}

            <div className="form-actions">
              <button className="btn-cancel" onClick={() => { setTopic(''); setSelectedDoc(''); }}>
                Cancel
              </button>
              <button
                className="btn-summarize"
                onClick={handleCreateSummary}
                disabled={loading || !selectedDoc}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="spinner" /> Generating...
                  </>
                ) : (
                  'Summarize'
                )}
              </button>
            </div>
          </>
        )}
      </div>

      <div className="summaries-list-card">
        <div className="summaries-list-header">
          <div>
            <h3>Your Summaries</h3>
            <p>View and manage your previously generated summaries</p>
          </div>
          {summaries.length > 0 && (
            <button className="btn-delete-all" onClick={handleDeleteAll}>
              Delete All
            </button>
          )}
        </div>

        {summaries.length === 0 ? (
          <div className="summaries-empty">
            <div className="summaries-empty-icon"><FileText size={48} /></div>
            <h4>No summaries yet</h4>
            <p>Create your first summary to get started</p>
          </div>
        ) : (
          <div className="summaries-list">
            {summaries.map((summary) => (
              <div key={summary.id} className="summary-item">
                <div className="summary-info">
                  <span className="summary-icon">📄</span>
                  <div className="summary-details">
                    <h4>{summary.documentName}</h4>
                    <div className="summary-meta">
                      <span className="summary-type-badge">{summary.summaryTypeLabel}</span>
                      <span>•</span>
                      <span>{summary.topic}</span>
                      <span>•</span>
                      <span>{new Date(summary.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
                <div className="summary-actions">
                  <button
                    className="btn-view"
                    onClick={() => setViewingSummary(summary)}
                  >
                    View
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDeleteSummary(summary.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loading Modal */}
      {loading && (
        <div className="loading-modal-overlay">
          <div className="loading-modal">
            <Loader2 size={32} className="loading-spinner" />
            <div className="loading-steps">
              {loadingSteps.map((step, idx) => (
                <div 
                  key={idx} 
                  className={`loading-step ${idx === loadingStep ? 'active' : idx < loadingStep ? 'completed' : ''}`}
                >
                  <div className="step-indicator">
                    {idx < loadingStep ? '✓' : idx + 1}
                  </div>
                  <span>{step}</span>
                </div>
              ))}
            </div>
            <p className="loading-hint">This takes about 10-15 seconds</p>
          </div>
        </div>
      )}

      {/* Summary View Modal */}
      {viewingSummary && (
        <div className="summary-view-modal" onClick={() => setViewingSummary(null)}>
          <div className="summary-view-content" onClick={(e) => e.stopPropagation()}>
            <div className="summary-view-header">
              <div>
                <h2>{viewingSummary.documentName}</h2>
                <div className="summary-view-meta">
                  <span className="type-badge">{viewingSummary.summaryTypeLabel}</span>
                  <span>{viewingSummary.topic}</span>
                </div>
              </div>
              <div className="summary-view-actions">
                <button 
                  className="btn-open-doc"
                  onClick={() => handleOpenDocument(viewingSummary.documentId)}
                >
                  <ExternalLink size={16} /> Open Document
                </button>
                <button className="btn-close" onClick={() => setViewingSummary(null)}>
                  <X size={24} />
                </button>
              </div>
            </div>
            
            {viewingSummary.qualityFlag && (
              <div className="quality-flag">
                <span>{viewingSummary.qualityFlag}</span>
              </div>
            )}
            
            <div className="summary-view-body">
              <div className="summary-text markdown-content">
                <ReactMarkdown>{viewingSummary.content}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Summaries;
